import { useState } from "react";
import { Controller, useFieldArray, useFormContext, useWatch, type FieldErrors } from "react-hook-form";
import clsx from "clsx";
import { GripVertical, Plus, X } from "lucide-react";
import { Autocomplete, Button, Card, Input, SegmentedControl, Select, Text } from "@marekl/adminer/components";
import { DecimalInput } from "../DecimalInput";
import { currencySign, formatMoney } from "../../lib/money";
import { searchProducts, type Product } from "../../lib/queries/products";
import type { Totals } from "../../lib/queries/documents";
import styles from "./LineItemsEditor.module.css";

/** One editable line; amounts are integers (quantity ×1000, price in minor units). */
export interface ItemValues {
  product_id: string | null;
  name: string;
  unit: string;
  quantity: number | null;
  unit_price: number | null;
  vat_rate: number;
  incl_vat: boolean;
}

export interface ItemsForm {
  currency: string;
  items: ItemValues[];
}

/** VAT rates in force in Czechia (2024–); historical documents may carry others. */
const RATES = [21, 12, 0];

export function newItem(vatRate: number, inclVat = false): ItemValues {
  return { product_id: null, name: "", unit: "ks", quantity: 1000, unit_price: null, vat_rate: vatRate, incl_vat: inclVat };
}

interface Props {
  companyId: string;
  vatPayer: boolean;
  defaultVatRate: number;
  /** Line totals incl. VAT from the server's preview, by item index. */
  lineTotals: (number | undefined)[];
  /** The server's totals for the lines on screen (undefined: nothing to add up yet). */
  totals: Pick<Totals, "vat_totals" | "total_incl_vat"> | undefined;
  pending: boolean;
}

/**
 * The document's lines, in the card's band (ADMINER_PROPOSALS A2, app-local):
 * a white row per item — drag handle, position, product lookup, exact
 * quantity and price, rate — and the server's totals under them. Rows reorder
 * by dragging the handle, or with the arrow keys on it.
 */
export function LineItemsEditor({ companyId, vatPayer, defaultVatRate, lineTotals, totals, pending }: Props) {
  const { control, register, setValue, getValues, formState } = useFormContext<ItemsForm>();
  const { fields, append, remove, move } = useFieldArray({ control, name: "items" });
  const currency = useWatch({ control, name: "currency" });
  // The rows' current values: `fields` is a snapshot that setValue does not
  // update (a picked product's rate must still have its option).
  const watched = useWatch({ control, name: "items" });
  const inclVat = watched?.[0]?.incl_vat ?? false;
  // A picked product brings its own price basis; the switch then shows
  // neither side until the user sets one for the whole document.
  const mixed = (watched ?? []).some((it) => it.incl_vat !== inclVat);
  const errors = (formState.errors.items ?? []) as (FieldErrors<ItemValues> | undefined)[];
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const rowClass = clsx(styles.row, !vatPayer && styles.rowNoVat);

  function pickProduct(index: number, p: Product) {
    const opts = { shouldDirty: true, shouldValidate: true } as const;
    setValue(`items.${index}.product_id`, p.id, opts);
    setValue(`items.${index}.name`, p.name, opts);
    setValue(`items.${index}.unit`, p.unit, opts);
    setValue(`items.${index}.unit_price`, p.price.value, opts);
    setValue(`items.${index}.vat_rate`, vatPayer ? p.price.vat_rate : 0, opts);
    setValue(`items.${index}.incl_vat`, p.price.incl_vat, opts);
  }

  // Prices with or without VAT: one switch for the whole document.
  function setInclVat(next: boolean) {
    getValues("items").forEach((_, i) => setValue(`items.${i}.incl_vat`, next, { shouldDirty: true }));
  }

  return (
    <Card.Band className={styles.band}>
      <div className={styles.bandHead}>
        <h2 className={styles.heading}>Položky</h2>
        {vatPayer && (
          <SegmentedControl
            size="sm"
            aria-label="Ceny položek"
            options={[
              { value: "excl", label: "ceny bez DPH" },
              { value: "incl", label: "s DPH" },
            ]}
            value={mixed ? "" : inclVat ? "incl" : "excl"}
            onChange={(v) => setInclVat(v === "incl")}
          />
        )}
        {vatPayer && mixed && <Text variant="muted">položky mají ceny s DPH i bez</Text>}
      </div>
      <div className={clsx(rowClass, styles.head)} aria-hidden="true">
        <span />
        <span />
        <span>Název</span>
        <span className={styles.num}>Množství</span>
        <span>Jedn.</span>
        <span className={styles.num}>Cena za jedn.</span>
        {vatPayer && <span>DPH</span>}
        <span className={styles.num}>Celkem</span>
        <span />
      </div>
      <ol className={styles.list}>
        {fields.map((field, i) => {
          const e = errors[i];
          const total = lineTotals[i];
          return (
            <li
              key={field.id}
              className={clsx(rowClass, styles.item, dragOver === i && dragFrom !== i && styles.dropTarget)}
              data-testid="line-item"
              onDragOver={(ev) => {
                if (dragFrom === null) return;
                ev.preventDefault();
                setDragOver(i);
              }}
              onDrop={(ev) => {
                ev.preventDefault();
                if (dragFrom !== null && dragFrom !== i) move(dragFrom, i);
                setDragFrom(null);
                setDragOver(null);
              }}
            >
              <span
                className={styles.handle}
                draggable={fields.length > 1}
                onDragStart={(ev) => {
                  // Firefox starts no drag without data.
                  ev.dataTransfer.setData("text/plain", String(i));
                  ev.dataTransfer.effectAllowed = "move";
                  setDragFrom(i);
                }}
                onDragEnd={() => {
                  setDragFrom(null);
                  setDragOver(null);
                }}
              >
                <Button
                  type="button"
                  iconOnly
                  size="sm"
                  weight="ghost"
                  aria-label={`Přesunout položku ${i + 1} (šipkami nahoru a dolů)`}
                  disabled={fields.length === 1}
                  startIcon={<GripVertical size={15} />}
                  onKeyDown={(ev) => {
                    if (ev.key === "ArrowUp" && i > 0) {
                      ev.preventDefault();
                      move(i, i - 1);
                    } else if (ev.key === "ArrowDown" && i < fields.length - 1) {
                      ev.preventDefault();
                      move(i, i + 1);
                    }
                  }}
                />
              </span>
              <span className={styles.pos}>{i + 1}</span>
              <Controller
                name={`items.${i}.name`}
                control={control}
                rules={{ validate: (v) => v.trim() !== "" || "Povinné pole" }}
                render={({ field: f, fieldState }) => (
                  <Autocomplete<Product>
                    aria-label={`Položka ${i + 1}`}
                    name={f.name}
                    ref={f.ref}
                    onBlur={f.onBlur}
                    value={f.value}
                    placeholder="Název položky nebo produktu"
                    onChange={(v) => {
                      f.onChange(v);
                      setValue(`items.${i}.product_id`, null);
                    }}
                    loadSuggestions={(q) => searchProducts(companyId, q)}
                    minChars={2}
                    getSuggestionLabel={(p) => p.name}
                    getSuggestionKey={(p) => p.id}
                    renderSuggestion={(p) => (
                      <span className={styles.suggestion}>
                        <span>{p.name}</span>
                        <Text variant="muted">
                          {formatMoney(p.price.value, p.price.currency)}
                          {p.unit && ` / ${p.unit}`}
                        </Text>
                      </span>
                    )}
                    onSelectSuggestion={(p) => pickProduct(i, p)}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name={`items.${i}.quantity`}
                control={control}
                rules={{ validate: (v) => (v !== null && v !== 0) || "Zadejte množství" }}
                render={({ field: f, fieldState }) => (
                  <DecimalInput aria-label={`Množství ${i + 1}`} scale={3} allowNegative value={f.value} onChange={f.onChange} onBlur={f.onBlur} error={fieldState.error?.message ?? e?.quantity?.message} className={styles.numInput} />
                )}
              />
              <Input aria-label={`Jednotka ${i + 1}`} {...register(`items.${i}.unit`)} error={e?.unit?.message} />
              <Controller
                name={`items.${i}.unit_price`}
                control={control}
                rules={{ validate: (v) => v !== null || "Zadejte cenu" }}
                render={({ field: f, fieldState }) => (
                  <DecimalInput aria-label={`Cena za jednotku ${i + 1}`} scale={2} suffix={currencySign(currency)} allowNegative value={f.value} onChange={f.onChange} onBlur={f.onBlur} error={fieldState.error?.message ?? e?.unit_price?.message} className={styles.numInput} />
                )}
              />
              {vatPayer && (
                <Select aria-label={`Sazba DPH ${i + 1}`} {...register(`items.${i}.vat_rate`, { valueAsNumber: true })} error={e?.vat_rate?.message}>
                  {Array.from(new Set([...RATES, fields[i].vat_rate, watched?.[i]?.vat_rate ?? fields[i].vat_rate])).map((r) => (
                    <option key={r} value={r}>{r} %</option>
                  ))}
                </Select>
              )}
              <div className={styles.total} aria-label={`Celkem ${i + 1}`}>
                {total === undefined ? "—" : formatMoney(total, currency)}
              </div>
              <Button
                type="button"
                iconOnly
                size="sm"
                weight="ghost"
                aria-label={`Odebrat položku ${i + 1}`}
                disabled={fields.length === 1}
                startIcon={<X size={15} />}
                onClick={() => remove(i)}
              />
            </li>
          );
        })}
      </ol>
      <div className={styles.foot}>
        <Button
          type="button"
          size="sm"
          weight="outline"
          startIcon={<Plus size={14} aria-hidden="true" />}
          onClick={() => append(newItem(vatPayer ? defaultVatRate : 0, inclVat))}
        >
          přidat položku
        </Button>
        <div className={styles.totals} aria-live="polite" aria-busy={pending}>
          {totals && vatPayer &&
            totals.vat_totals.map((r) => (
              <div key={r.vat_rate} className={styles.totalsRows}>
                <div><span>Základ {r.vat_rate} %</span><b>{formatMoney(r.base, currency)}</b></div>
                <div><span>DPH {r.vat_rate} %</span><b>{formatMoney(r.vat, currency)}</b></div>
              </div>
            ))}
          <div className={styles.grand}>
            <span>Celkem k úhradě</span>
            <b data-testid="grand-total">{totals ? formatMoney(totals.total_incl_vat, currency) : "—"}</b>
          </div>
        </div>
      </div>
    </Card.Band>
  );
}
